import React, { useEffect, useState } from 'react';
import {
  ArrowUpRight,
  ArrowRight,
  Globe,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
} from 'lucide-react';
import logo from '../assets/images/atadaboard-logo.png';
import {
  SITE_ORIGIN,
  resetDocumentMetadata,
  setDocumentMetadata,
} from '../utils/documentMeta';
import { APP_LINKS, SOCIAL_LINKS } from '../data/site';
import '../styles/landing.css';

const SOCIAL_ICONS = { Instagram, Facebook, Linkedin, Youtube };

/*
  This page is the target of a link the school sends to parents and staff, so
  most visitors arrive on a phone with one intention: install the app. The
  layout puts the two app stores in reach immediately and keeps everything
  else short.

  It shares the landing page's tokens (src/styles/landing.css) and is wrapped
  in `.ab-page`, so typography, colour, radii and the box-sizing reset all
  match the rest of the site.
*/
const STORES = [
  {
    id: 'ios',
    platform: 'iPhone and iPad',
    store: 'App Store',
    body: 'Requires iOS 13 or later.',
    url: APP_LINKS.ios,
    match: 'ios',
  },
  {
    id: 'android',
    platform: 'Android',
    store: 'Google Play',
    body: 'Phones and tablets.',
    url: APP_LINKS.android,
    match: 'android',
  },
];

/*
  A hint, never a redirect: the matching card gets a small note, and all three
  options stay fully visible and clickable whatever this returns. iPadOS
  reports itself as a Mac, so it is identified by the touch points instead.
*/
const detectPlatform = () => {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent || '';
  const isIpad =
    /iPad/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);

  if (/iPhone|iPod/.test(ua) || isIpad) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return null;
};

const DownloadRequest = () => {
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  // This route has its own search listing, so it needs its own title,
  // description and canonical rather than inheriting the landing page's.
  useEffect(() => {
    setDocumentMetadata({
      title: 'Download the AtadaBoard School Management App | iOS, Android & Web',
      description:
        'Download AtadaBoard, the school management software used by schools in Ghana, on iPhone, iPad and Android, or open the web app from any browser.',
      canonicalUrl: `${SITE_ORIGIN}/download`,
      ogUrl: `${SITE_ORIGIN}/download`,
    });
    return resetDocumentMetadata;
  }, []);

  return (
    <div className="ab-page ab-dl">
      <a className="ab-skip-link" href="#main">
        Skip to content
      </a>

      <header className="ab-dl__bar">
        <div className="ab-shell ab-dl__barInner">
          <a href="/" className="ab-dl__brand" aria-label="AtadaBoard home">
            <img src={logo} alt="AtadaBoard" width="2437" height="732" />
          </a>
          <a href="/" className="ab-textlink ab-dl__back">
            Visit the site
            <ArrowRight size={15} strokeWidth={2} />
          </a>
        </div>
      </header>

      <main id="main" className="ab-dl__main">
        <div className="ab-shell ab-dl__inner">
          <h1 className="ab-display ab-dl__title">Get the AtadaBoard app</h1>
          <p className="ab-lead ab-dl__lead">
            Your child’s school in your pocket. Attendance, report cards, school bills and messages
            from the school, in one app.
          </p>

          <div className="ab-dl__stores">
            {STORES.map((store) => {
              const isLikely = platform === store.match;
              return (
                <a
                  key={store.id}
                  href={store.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`ab-dl__store${isLikely ? ' is-likely' : ''}`}
                >
                  {isLikely && <span className="ab-dl__hint">For your device</span>}
                  <span className="ab-dl__platform">{store.platform}</span>
                  <span className="ab-dl__body">{store.body}</span>
                  <span className="ab-dl__action">
                    {store.store}
                    <ArrowUpRight size={17} strokeWidth={2} />
                  </span>
                </a>
              );
            })}
          </div>

          {/*
            The web console is a different kind of option: nothing to install,
            and it is what most administrators and teachers use. It gets its own
            full-width row rather than a third identical card.
          */}
          <a
            href={APP_LINKS.web}
            target="_blank"
            rel="noopener noreferrer"
            className="ab-dl__web"
          >
            <span className="ab-dl__webIcon">
              <Globe size={20} strokeWidth={1.75} />
            </span>
            <span className="ab-dl__webText">
              <span className="ab-dl__platform">Or use it in your browser</span>
              <span className="ab-dl__body">
                Nothing to install. Sign in at {APP_LINKS.web.replace('https://', '')}
              </span>
            </span>
            <ArrowUpRight size={18} strokeWidth={2} className="ab-dl__webArrow" />
          </a>

          <div className="ab-dl__notes">
            <p>
              <strong>Signing in for the first time?</strong> Use the phone number or email address
              your school has on record for you.
            </p>
            <p>
              <strong>School not on AtadaBoard yet?</strong>{' '}
              <a href="/#demo-form">Book a demo</a> and we will set it up with you.
            </p>
          </div>
        </div>
      </main>

      <footer className="ab-dl__footer">
        <div className="ab-shell ab-dl__footerInner">
          <p>© {new Date().getFullYear()} AtadaBoard by Atada Technologies</p>

          <div className="ab-dl__social">
            {SOCIAL_LINKS.map((social) => {
              const Icon = SOCIAL_ICONS[social.icon];
              if (!Icon) return null;
              return (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`AtadaBoard on ${social.name}`}
                >
                  <Icon size={18} strokeWidth={1.75} />
                </a>
              );
            })}
          </div>
        </div>
      </footer>

      <style jsx>{`
        .ab-dl {
          display: flex;
          flex-direction: column;
          min-height: 100dvh;
        }

        .ab-dl__bar {
          border-bottom: 1px solid var(--ab-line);
        }

        .ab-dl__barInner {
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .ab-dl__brand {
          display: flex;
          align-items: center;
          border-radius: 6px;
        }

        .ab-dl__brand img {
          height: 28px;
          width: auto;
          display: block;
        }

        .ab-dl__brand:focus-visible,
        .ab-dl__back:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 4px;
        }

        .ab-dl__main {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          padding-block: clamp(2.5rem, 6vw, 5rem);
          overflow: clip;
        }

        /* The same soft wash the landing hero uses, so the two pages match. */
        .ab-dl__main::before {
          content: '';
          position: absolute;
          inset: -40% -10% auto -10%;
          height: 160%;
          background: radial-gradient(
            60% 55% at 50% 12%,
            var(--ab-blue-50) 0%,
            rgba(238, 245, 251, 0) 70%
          );
          pointer-events: none;
        }

        .ab-dl__inner {
          position: relative;
          max-width: 720px;
        }

        .ab-dl__title {
          font-size: clamp(2.1rem, 5vw, 3rem);
          margin-bottom: 0.875rem;
        }

        .ab-dl__lead {
          max-width: 46ch;
          margin-bottom: clamp(2.25rem, 5vw, 3rem);
        }

        /* Two stores side by side; the browser option below gets its own shape. */
        .ab-dl__stores {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .ab-dl__store {
          position: relative;
          display: flex;
          flex-direction: column;
          padding: clamp(1.25rem, 3vw, 1.75rem);
          background: var(--ab-paper);
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          text-decoration: none;
          box-shadow: var(--ab-shadow-sm);
          transition: border-color 0.25s var(--ab-ease), box-shadow 0.25s var(--ab-ease),
            transform 0.25s var(--ab-ease);
        }

        .ab-dl__store:hover {
          border-color: var(--ab-blue-100);
          box-shadow: var(--ab-shadow-md);
          transform: translateY(-2px);
        }

        .ab-dl__store:active { transform: translateY(0); }

        .ab-dl__store:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 3px;
        }

        .ab-dl__store.is-likely {
          border-color: var(--ab-blue);
          background: linear-gradient(160deg, var(--ab-blue-50) 0%, var(--ab-paper) 60%);
        }

        .ab-dl__hint {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--ab-blue);
          margin-bottom: 0.375rem;
        }

        .ab-dl__platform {
          font-family: var(--ab-font-display);
          font-size: 1.1875rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--ab-ink);
        }

        .ab-dl__body {
          font-size: 0.9375rem;
          line-height: 1.5;
          color: var(--ab-ink-3);
          margin-top: 0.25rem;
        }

        /* Pinned to the bottom so both cards' actions sit on one line. */
        .ab-dl__action {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          margin-top: auto;
          padding-top: 1.25rem;
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--ab-blue);
        }

        .ab-dl__web {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.125rem clamp(1.25rem, 3vw, 1.75rem);
          background: var(--ab-paper);
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          text-decoration: none;
          box-shadow: var(--ab-shadow-sm);
          transition: border-color 0.25s var(--ab-ease), box-shadow 0.25s var(--ab-ease);
        }

        .ab-dl__web:hover {
          border-color: var(--ab-blue-100);
          box-shadow: var(--ab-shadow-md);
        }

        .ab-dl__web:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 3px;
        }

        .ab-dl__webIcon {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border-radius: var(--ab-r-inner);
          background: var(--ab-blue-100);
          color: var(--ab-blue);
        }

        .ab-dl__webText {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .ab-dl__webText .ab-dl__platform { font-size: 1.0625rem; }

        .ab-dl__webArrow {
          flex-shrink: 0;
          color: var(--ab-blue);
        }

        .ab-dl__notes {
          margin-top: clamp(2rem, 4vw, 2.75rem);
          padding-top: 1.5rem;
          border-top: 1px solid var(--ab-line);
          display: grid;
          gap: 0.625rem;
        }

        .ab-dl__notes p {
          font-size: 0.9375rem;
          line-height: 1.6;
          color: var(--ab-ink-2);
          max-width: 62ch;
        }

        .ab-dl__notes strong {
          font-weight: 600;
          color: var(--ab-ink);
        }

        .ab-dl__notes a {
          color: var(--ab-blue);
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .ab-dl__notes a:hover { color: var(--ab-blue-hover); }

        .ab-dl__notes a:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 3px;
          border-radius: 3px;
        }

        .ab-dl__footer {
          background: var(--ab-navy);
          color: rgba(255, 255, 255, 0.78);
        }

        .ab-dl__footerInner {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding-block: 1.375rem;
          font-size: 0.875rem;
        }

        .ab-dl__social { display: flex; gap: 0.5rem; }

        .ab-dl__social a {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--ab-r-pill);
          border: 1px solid rgba(255, 255, 255, 0.18);
          color: rgba(255, 255, 255, 0.78);
          transition: background-color 0.2s var(--ab-ease), color 0.2s var(--ab-ease),
            border-color 0.2s var(--ab-ease);
        }

        .ab-dl__social a:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.4);
          color: #fff;
        }

        .ab-dl__social a:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
        }

        @media (max-width: 560px) {
          .ab-dl__stores { grid-template-columns: minmax(0, 1fr); }
          .ab-dl__footerInner { justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
};

export { DownloadRequest };
